import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'components/ui/button'
import { ListTodo, CheckCircle2, Clock, Users, Calendar } from 'lucide-react'
import { advanceOnboardingStep, ONBOARDING_STEPS } from 'slices/onboardingSlice'
import CreateTaskCNModal from 'screens/taskscn/CreateTaskCNModal'

interface TaskTourStepProps {
	user: any
	onSkip: () => void
}

const TaskTourStep: React.FC<TaskTourStepProps> = ({ user, onSkip }) => {
	const dispatch = useDispatch()
	const { saving, createdProjectId } = useSelector((state: any) => state.onboarding)
	const [showCreateTask, setShowCreateTask] = useState(false)

	const handleNext = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.INVITE,
				userId: user._id,
			}),
		)
	}

	const handleBack = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.PROJECT,
				userId: user._id,
			}),
		)
	}

	const handleTaskCreated = () => {
		handleNext()
	}

	const features = [
		{
			icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,
			title: 'Track Milestones',
			desc: 'Create tasks for lease deadlines, inspections, and deliverables',
		},
		{
			icon: <Users className="w-4 h-4 text-blue-500" />,
			title: 'Assign Team Members',
			desc: 'Delegate tasks to team members with priority levels (P1-P5)',
		},
		{
			icon: <Clock className="w-4 h-4 text-orange-500" />,
			title: 'Due Dates & Effort',
			desc: 'Set due dates and track effort for every task',
		},
		{
			icon: <Calendar className="w-4 h-4 text-purple-500" />,
			title: 'Multiple Views',
			desc: 'View tasks as cards, a table, or on a calendar',
		},
	]

	return (
		<div className="p-6">
			<div className="flex items-center gap-2 mb-1">
				<ListTodo className="w-5 h-5 text-[#0080fe]" />
				<h2 className="text-lg font-semibold text-[#074f9b]">Task Management</h2>
			</div>
			<p className="text-sm text-gray-500 mb-5">
				Tasks help you track milestones, assignments, and deadlines for your deals.
			</p>

			<div className="space-y-3 mb-6">
				{features.map((feature, i) => (
					<div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
						<div className="mt-0.5 flex-shrink-0">{feature.icon}</div>
						<div>
							<p className="text-sm font-medium text-gray-800">{feature.title}</p>
							<p className="text-xs text-gray-500">{feature.desc}</p>
						</div>
					</div>
				))}
			</div>

			<div className="flex flex-col gap-2">
				{createdProjectId && (
					<Button
						onClick={() => setShowCreateTask(true)}
						variant="text"
						isLarge
						className="w-full"
					>
						Create Your First Task
					</Button>
				)}
				<div className="flex gap-2">
					<Button variant="ghost" onClick={handleBack} disabled={saving} className="flex-1">
						Back
					</Button>
					<Button onClick={handleNext} disabled={saving} className="flex-1">
						{createdProjectId ? "I'll Do This Later" : 'Next'}
					</Button>
				</div>
			</div>

			{showCreateTask && (
				<CreateTaskCNModal
					open={showCreateTask}
					onClose={() => setShowCreateTask(false)}
					defaultProjectId={createdProjectId}
				/>
			)}
		</div>
	)
}

export default TaskTourStep
